import i18n from '@/translations/i18n'
import {z} from 'zod'
import {PatientAgeRange} from '@/types'
import {NotificationUsersConfirmationValidationSchema} from '../users/types'
import {Diagnosis, ProjectStatus} from '@/features/projects/types'

export const TreatmentRequestDetailsUrlParams = z.object({id: z.string().min(1)})
export const TreatmentRequestDetailsSearchParams = z.object({
    sidebarSection: z.enum(['activities']).default('activities')
})

type TreatmentRequestDisease = {
    id: string
    name: string
    parent: TreatmentRequestDisease | null
    serviceType: Exclude<PatientAgeRange, 'evolutionary_age_adults'>
}
export const TreatmentRequestDisease: z.ZodType<TreatmentRequestDisease> = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    parent: z.lazy(() => TreatmentRequestDisease).nullable(),
    serviceType: z.enum(PatientAgeRange.exclude(['evolutionary_age_adults']).options)
})

export const DiseaseBase = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    serviceType: z.enum(PatientAgeRange.exclude(['evolutionary_age_adults']).options)
})
export type DiseaseBase = z.infer<typeof DiseaseBase>

export const DiseaseSmall = DiseaseBase.and(
    z.object({
        rootParent: z.lazy(() => DiseaseBase)
    })
)
export type DiseaseSmall = z.infer<typeof DiseaseSmall>

export type DiseaseExtended = DiseaseBase & {
    children?: DiseaseExtended[]
    parent?: DiseaseExtended | null
}
export const DiseaseExtended: z.ZodType<DiseaseExtended> = DiseaseBase.and(
    z.object({
        children: z
            .lazy(() => DiseaseExtended)
            .array()
            .optional(),
        parent: z.lazy(() => DiseaseExtended).nullish()
    })
)

export const TreatmentRequestAuthorization = z.enum(['asl', 'hospital_doctor'])

export const TreatmentRequestType = z.enum(['new_takeover', 'treatment_resumption', 'cycle_resumption'])
export type TreatmentRequestType = z.infer<typeof TreatmentRequestType>

export const TreatmentRequestStatus = z.enum([
    'in_review',
    'waiting_list',
    'in_progress',
    'accepted',
    'rejected',
    'deprecated'
])

export const RequestTreatmentAdultsScoreType = z.enum([
    'social_data',
    'subtype',
    'comorbidity',
    'objectives',
    'reporting',
    'acuteness'
])
export type RequestTreatmentAdultsScoreType = z.infer<typeof RequestTreatmentAdultsScoreType>

export const RequestTreatmentEvoutionaryAgeScore = z.enum([
    'social_discomfort',
    'previous_treatments',
    'severity_pathology',
    'age_groups_pathology',
    'neuromuscular_index'
])
export type RequestTreatmentEvoutionaryAgeScore = z.infer<typeof RequestTreatmentEvoutionaryAgeScore>

export const RequestTreatmentSirScore = z.enum(['precocity'])
export type RequestTreatmentSirScore = z.infer<typeof RequestTreatmentSirScore>

export const TreatmentRequestScore = z.object({
    type: z.enum([
        ...RequestTreatmentAdultsScoreType.options,
        ...RequestTreatmentEvoutionaryAgeScore.options,
        ...RequestTreatmentSirScore.options
    ]),
    score: z.number().nullable()
})
export type TreatmentRequestScore = z.infer<typeof TreatmentRequestScore>

export const TreatmentRequestRejectedReason = z.enum([
    'request_expired',
    'request_not_compatible',
    'taken_over_by_another_center',
    'deceased',
    'other'
])

export const TreatmentRequestSmall = z.object({
    acceptedAt: z.string().min(1).nullable(),
    authorization: TreatmentRequestAuthorization.nullable(),
    authorizationDate: z.string().min(1).nullable(),
    createdAt: z.string().min(1),
    disease: TreatmentRequestDisease.nullable(),
    // Diagnosis code, in whichever classification it was picked — its `version` says which.
    // Optional at creation, mandatory (warning-only) once the request is in_review.
    // nullish() so parsing tolerates the field being absent while backend support is being introduced.
    diagnosisCode: Diagnosis.nullish(),
    expirationDate: z.string().min(1).nullable(),
    fieldsToCompile: z.string().array(),
    id: z.string().min(1),
    rejectedAt: z.string().min(1).nullable(),
    patientId: z.string().min(1),
    patientFullName: z.string().min(1),
    patientAslName: z.string().min(1).nullable(),
    patientAslDistrictName: z.string().min(1).nullable(),
    rejectedReason: TreatmentRequestRejectedReason.nullable(),
    sector: z.enum(['outpatient', 'domiciliary']),
    serviceType: z.enum(['evolutionary_age', 'adults']),
    status: TreatmentRequestStatus,
    totalScore: z.number().int().nullable(),
    type: TreatmentRequestType,
    referringDoctor: z
        .object({
            id: z.string().min(1),
            fullName: z.string().min(1)
        })
        .nullable(),
    therapists: z
        .array(
            z.object({
                id: z.string().min(1),
                fullName: z.string().min(1)
            })
        )
        .nullable()
})
export type TreatmentRequestSmall = z.infer<typeof TreatmentRequestSmall>

const FollowUp = z.object({
    id: z.string().min(1),
    date: z.string(),
    notes: z.string().nullable()
})
export type FollowUp = z.infer<typeof FollowUp>

export const TreatmentRequestExtended = TreatmentRequestSmall.and(
    z.object({
        code: z.number(),
        expirationDate: z.string().nullable(),
        diseaseOnsetDate: z.string().nullable(),
        lastTreatmentDate: z.string().nullable(),
        lastInterventionModality: z.string().nullable(), // todo: convert this to enum,
        scores: z.array(TreatmentRequestScore),
        notes: z.string().nullable(),
        project: z
            .object({
                id: z.string().min(1),
                code: z.number(),
                status: z.enum(ProjectStatus.options)
            })
            .nullable(),
        followups: z.array(FollowUp)
    })
)
export type TreatmentRequestExtended = z.infer<typeof TreatmentRequestExtended>

export const TreatmentRequestsColumn = z.enum([
    'select',
    'fullName',
    'serviceType',
    'sector',
    'status',
    'createdAt',
    'expirationDate',
    'acceptedAt',
    'rejectedAt',
    'rejectedReason',
    'authorization',
    'authorizationDate',
    'referringDoctorId',
    'therapistId',
    'diagnosisId',
    'type',
    'score',
    'residenceAslDistrictId',
    'moreActions'
])
export type TreatmentRequestsColumn = z.infer<typeof TreatmentRequestsColumn>

export const TreatmentRequestsSearchParams = z.object({
    limit: z.coerce.number().int().positive().default(50),
    page: z.coerce.number().int().positive().default(1),
    search: z.string().trim().optional(),
    filters: z.string().optional(),
    sorts: z.string().optional(),
    columnVisibility: z.string().optional()
})

/*Validation schemas*/
export const TreatmentRequestPatientValidationSchema = z.object({
    authorization: z
        .enum(TreatmentRequestAuthorization.options, {required_error: i18n.t('errors:required')})
        .nullable(),
    authorizationToRequest: z.boolean().default(false),
    authorizationDate: z
        .date({
            errorMap: issue => ({
                message: issue.code == 'invalid_date' ? i18n.t('errors:invalid_date') : i18n.t('errors:required')
            })
        })
        .nullish(),
    patient: z.object(
        {
            id: z.string().min(1),
            fullName: z.string().min(1)
        },
        {required_error: i18n.t('errors:required')}
    ),
    serviceType: PatientAgeRange.exclude(['evolutionary_age_adults']),
    project: z
        .object({
            id: z.string().min(1)
        })
        .nullable()
})
export type TreatmentRequestPatientValidationSchema = z.infer<typeof TreatmentRequestPatientValidationSchema>

export const TreatmentRequestAuthorizationValidationSchema = TreatmentRequestPatientValidationSchema.pick({
    authorization: true,
    authorizationToRequest: true,
    authorizationDate: true,
    status: true
}).refine(
    values =>
        !values.authorization ||
        values.authorizationDate ||
        (values.authorizationToRequest && !values.authorizationDate),
    {
        message: i18n.t('errors:required'),
        path: ['authorizationDate']
    }
)
export type TreatmentRequestAuthorizationValidationSchema = z.infer<
    typeof TreatmentRequestAuthorizationValidationSchema
>

export const TreatmentRequestScoresValidationSchema = z.object({
    scores: z.array(TreatmentRequestScore),
    diseases: z.array(DiseaseExtended)
})
export type TreatmentRequestScoresValidationSchema = z.infer<typeof TreatmentRequestScoresValidationSchema>

export const TreatmentRequestTreatmentValidationSchema = TreatmentRequestPatientValidationSchema.pick({
    patient: true
})
    .and(
        z
            .object({
                project: z
                    .object({
                        id: z.string().min(1)
                    })
                    .nullable(),
                type: z.enum(TreatmentRequestSmall.shape['type'].options, {required_error: i18n.t('errors:required')})
            })
            .refine(
                values =>
                    values.type == 'new_takeover' ||
                    (!!values.project && (values.type == 'cycle_resumption' || values.type == 'treatment_resumption')),
                {
                    message: i18n.t('treatment_requests:type_error'),
                    path: ['type']
                }
            )
    )
    .and(
        z.object({
            sector: z.string({required_error: i18n.t('errors:required')}).min(1, i18n.t('errors:required'))
        })
    )
    .and(TreatmentRequestAuthorizationValidationSchema)
    .and(TreatmentRequestScoresValidationSchema)
export type TreatmentRequestTreatmentValidationSchema = z.infer<typeof TreatmentRequestTreatmentValidationSchema>

export const TreatmentRequestNotesValidationSchema = z.object({
    notes: z
        .string({required_error: i18n.t('errors:required'), invalid_type_error: i18n.t('errors:required')})
        .trim()
        .min(1, {message: i18n.t('errors:required')})
})
export type TreatmentRequestNotesValidationSchema = z.infer<typeof TreatmentRequestNotesValidationSchema>

export const TreatmentRequestDiagnosisValidationSchema = z.object({
    diseases: z.array(DiseaseExtended).min(1, {message: i18n.t('errors:required')}),
    diseaseOnsetDate: z.date().nullish(),
    // Optional in every phase at the form level; the "required when in_review" rule is a non-blocking detail warning.
    diagnosisCode: Diagnosis.nullish(),
    scores: z.array(TreatmentRequestScore)
})
export type TreatmentRequestDiagnosisValidationSchema = z.infer<typeof TreatmentRequestDiagnosisValidationSchema>

export const TreatmentRequestValidationSchema = TreatmentRequestPatientValidationSchema.and(
    TreatmentRequestTreatmentValidationSchema
)
    .and(TreatmentRequestDiagnosisValidationSchema)
    .and(TreatmentRequestScoresValidationSchema)
    .and(TreatmentRequestNotesValidationSchema)
export type TreatmentRequestValidationSchema = z.infer<typeof TreatmentRequestValidationSchema>

/**
 * Main schema that combines all the subform schemas with notification settings
 */
export const CreateTreatmentRequestValidationSchema = TreatmentRequestValidationSchema.and(
    NotificationUsersConfirmationValidationSchema
)
export type CreateTreatmentRequestValidationSchema = z.infer<typeof CreateTreatmentRequestValidationSchema>

export const TreatmentRequestTherapistDoctor = z.object({
    id: z.string().min(1),
    fullName: z.string().min(1),
    areaName: z.string().min(1).optional(),
    distance: z.number().optional(),
    contacts: z.array(
        z.object({
            type: z.enum(['mobile_phone', 'email', 'telephone']),
            contact: z.string().min(1)
        })
    ),
    specialization: z.object({
        name: z.string().min(1)
    }),
    priCount: z.number().int().nonnegative().optional(),
    availableSlots: z.number().int().nonnegative().optional()
})
export type TreatmentRequestTherapistDoctor = z.infer<typeof TreatmentRequestTherapistDoctor>

export type OnSubmitFollowUp<T> = T extends FollowUp
    ? {date?: Date; notes?: string | null}
    : {date: Date; notes?: string | null}

export const TreatmentRequestRejectedReasonValidationSchema = z
    .object({
        rejectedReason: TreatmentRequestRejectedReason
    })
    .merge(NotificationUsersConfirmationValidationSchema)

export type TreatmentRequestRejectedReasonValidationSchema = z.infer<
    typeof TreatmentRequestRejectedReasonValidationSchema
>
